import { TEulaAcceptance, TRepresentativeFormData } from '@/modules/onboarding/organization/types';
import { TRosterItemPerson, TRosterTabSelection, TRosterUserPeerSupportCertificateInfo } from '@/types/Roster.types';
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';

export type TParsedRosterList = TRosterItemPerson[] | null;
export type TPeerSupportCertificateInfoMap = Map<number, TRosterUserPeerSupportCertificateInfo> | null;

type TOnboardingState = {
  step: number;
  isCompleted: boolean;
  nextStep: () => void;
  prevStep: () => void;
  reset: () => void;
  // onSubmitRepresentativeForm
  representativeForm: TRepresentativeFormData | null;
  eulaAcceptance: TEulaAcceptance | null;
  setRepresentativeForm: (data: TRepresentativeFormData, eulaAcceptance: TEulaAcceptance | null) => void;
  // onSubmitLogo
  logoFile: File | null;
  logoFilePreview: string | null;
  setLogoFile: (file: File | null, filePreview: string | null) => void;
  industry: keyof typeof import('@/utils/demographicDataMappers').OccupationFeBe | null;
  industryOther: string;
  setIndustry: (industry: TOnboardingState['industry'], industryOther: string) => void;
  // onSubmitRosterTemplate
  rosterCSVFile: File | null;
  setRosterCSVFile: (newFile: File | null) => void;
  rosterCSVPreview: string | null;
  setRosterCSVPreview: (newPreview: string | null) => void;
  rosterCSV: TParsedRosterList;
  setRosterCSV: (newRoster: TParsedRosterList) => void;
  rosterManual: TParsedRosterList;
  setRosterManual: (newRoster: TParsedRosterList) => void;
  peerSupportCertificateDataCSV: TPeerSupportCertificateInfoMap;
  setPeerSupportCertificateDataCSV: (newData: TPeerSupportCertificateInfoMap) => void;
  peerSupportCertificateDataManual: TPeerSupportCertificateInfoMap;
  setPeerSupportCertificateDataManual: (newData: TPeerSupportCertificateInfoMap) => void;
  selectedRosterTab: TRosterTabSelection;
  setSelectedRosterTab: (tab: TRosterTabSelection) => void;
  CSVTabStep: 0 | 1;
  setCSVTabStep: (newCSVTabStep: 0 | 1) => void;
};

const initialState = {
  step: 0,
  isCompleted: false,
  representativeForm: null,
  eulaAcceptance: null,
  logoFile: null,
  logoFilePreview: null,
  industry: null,
  industryOther: '',
  rosterCSVFile: null,
  rosterCSVPreview: null,
  rosterCSV: null,
  rosterManual: null,
  peerSupportCertificateDataCSV: null,
  peerSupportCertificateDataManual: null,
  selectedRosterTab: 'manual' as TRosterTabSelection,
  CSVTabStep: 0 as const,
};

/**
 * State will persist even after page is refreshed.
 */
export const useOrganizationOnboardingStore = create<TOnboardingState>()(
  subscribeWithSelector(
    persist(
      (set) => ({
        ...initialState,
        nextStep: () => {
          set((state) => {
            if (!state.isCompleted) {
              return { ...state, step: state.step + 1 };
            }
            return state;
          });
        },
        prevStep: () =>
          set((state) => {
            if (state.step === 0) {
              return state;
            }
            return { ...state, step: state.step - 1 };
          }),
        reset: () => {
          set((state) => ({
            ...state,
            ...initialState,
          }));
        },
        setRepresentativeForm: (data: TRepresentativeFormData, eulaAcceptance: TEulaAcceptance | null) => {
          set((state) => ({
            ...state,
            representativeForm: data,
            eulaAcceptance,
          }));
        },
        setLogoFile: (file: File | null, filePreview: string | null) => {
          set((state) => ({
            ...state,
            logoFile: file,
            logoFilePreview: filePreview,
          }));
        },
        setIndustry: (industry, industryOther) => set({ industry, industryOther }),
        setRosterCSVFile: (newFile: File | null) => {
          set((state) => ({ ...state, rosterCSVFile: newFile }));
        },
        setRosterCSVPreview: (newPreview: string | null) => {
          set((state) => ({ ...state, rosterCSVPreview: newPreview }));
        },
        setPeerSupportCertificateDataCSV: (newData: TPeerSupportCertificateInfoMap) => {
          set((state) => ({
            ...state,
            peerSupportCertificateDataCSV: newData,
          }));
        },
        setPeerSupportCertificateDataManual: (newData: TPeerSupportCertificateInfoMap) => {
          set((state) => ({
            ...state,
            peerSupportCertificateDataManual: newData,
          }));
        },
        setRosterCSV: (roster: TParsedRosterList) => {
          set((state) => ({
            ...state,
            rosterCSV: roster,
          }));
        },
        setRosterManual: (roster: TParsedRosterList) => {
          set((state) => ({
            ...state,
            rosterManual: roster,
          }));
        },
        setSelectedRosterTab: (newSelectedRosterTab: TRosterTabSelection) => {
          set((state) => ({
            ...state,
            selectedRosterTab: newSelectedRosterTab,
          }));
        },
        setCSVTabStep: (newCSVTabStep: 0 | 1) => {
          set((state) => ({
            ...state,
            CSVTabStep: newCSVTabStep,
          }));
        },
      }),
      {
        name: 'organizationOnboardingStore',
        version: 2,
        migrate: (persistedState, version) => {
          const state = persistedState as TOnboardingState;
          if (version === 0) {
            return { ...state, step: state.step >= 1 && state.step <= 3 ? state.step + 1 : state.step };
          }
          if (version === 1) {
            return { ...state, step: state.step === 1 ? 2 : state.step === 2 ? 1 : state.step };
          }
          return state;
        },
      },
    ),
  ),
);
